import type { ReactElement } from "react";
import { useMemo } from "react";

import { translate } from "app/filters/i18nFilter";
import { RangeSelectWithFilter } from "app/pages/sales-intelligence/common-components/form-fields";
import type { IRange } from "app/pages/sales-intelligence/types/range";
import { commonKeys } from "app/pages/sales-intelligence/utils/i18n/common";

import { getFormattedValue } from "../../../utils/rangeValueFormatters";
import FilterClearButton from "../../FilterClearButton";

const RANGE_SELECT_WIDTH = 93;
const RANGE_SELECT_COLUMN_GAP = 9;

interface IRangeSelectWithClearProps {
    fieldName: string;
    value: Partial<IRange<number>> | undefined;
    buckets: readonly number[];
    formatValue: (value: number | string) => string;
    placeholderPrefix?: string;
    isDisabled?: boolean;
    isTopLayer?: boolean;
    onChange: ([min, max]: readonly number[]) => void;
    onClear: () => void;
}

const RangeSelectWithClear = ({
    fieldName,
    value,
    onChange,
    onClear,
    buckets,
    formatValue,
    placeholderPrefix,
    isDisabled = false,
    isTopLayer,
}: IRangeSelectWithClearProps): ReactElement => {
    const placeholderMin = placeholderPrefix
        ? `${placeholderPrefix} ${translate(commonKeys.filters.range.minPlaceholder)}`
        : translate(commonKeys.filters.range.minPlaceholder);

    const placeholderMax = placeholderPrefix
        ? `${placeholderPrefix} ${translate(commonKeys.filters.range.maxPlaceholder)}`
        : translate(commonKeys.filters.range.maxPlaceholder);

    const [minOptions, maxOptions] = useMemo(() => [buckets.slice(0, -1), buckets.slice(1)], [buckets]);

    const formater = (val: number) => getFormattedValue(formatValue, val);

    return (
        <RangeSelectWithFilter
            fieldName={fieldName}
            value={[value?.from, value?.to]}
            optionsMin={minOptions}
            optionsMax={maxOptions}
            placeholderMin={placeholderMin}
            placeholderMax={placeholderMax}
            formatValue={formater}
            isTopLayer={isTopLayer}
            onChange={onChange}
            width={RANGE_SELECT_WIDTH}
            columnGap={RANGE_SELECT_COLUMN_GAP}
            isDisabled={isDisabled}
        >
            <FilterClearButton
                isDisabled={!value}
                onClick={onClear}
            />
        </RangeSelectWithFilter>
    );
};

export default RangeSelectWithClear;
